export const supportedFileTypes = ["doc", "docx", "pdf", "msg", "xlsx", "png", "jpeg", "jpg"];

// Configurable character limits
export const COMMENTS_MAX_LENGTH = 1000;

const errorMessages = {
  browser: {
    protocolSetupForm: {
      lpo: {
          required: "Required Field"
      },
      protocolNumber: {
          required: "Required Field",
        helpText: "If you cannot find a protocol, please contact the CTSU Help Desk."
      },
      formPartType: {
          required: "Required Field"
      },
      validation: {
        error: "Error validating form. Please try again.",
        duplicate: (setupType, statusLabel) =>
          `This protocol already has a ${setupType} request in ${statusLabel} status.`,
        invalidTooltip: "Please complete required fields to continue.",
        validTooltip: "Initiate the new protocol request.",
        ancillaryDuplicate: "Ancillary study has duplicate records.",
        irocDuplicate: "IROC credentialing has duplicate records.",
        stepDuplicate: "Step information has duplicate records.",
        psrDuplicate: "PSR has duplicate records."
      },
      labels: {
        openRaveSetup: "OPEN/Rave setup",
        regulatorySetup: "Regulatory setup"
      }
    }
  },
  common: {
    fileUpload: {
      allowedFormats: `Only ${supportedFileTypes.map(ext => `.${ext}`).join(", ")} files are allowed`
    },
    comments: {
      max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`,
      required: "Required Field"
    },
    openRave: {
      max1000: "Cannot exceed 1000 characters."
    }
  },
  openRave: {
    infoForm: {
      requestType: {
          required: "Required Field",
        min: "Required Field"
      },
      listOfChanges: {
          required: "Required Field",
        min: "Required Field"
      },
      describeOtherChanges: {
          required: "Required Field",
        max: "Cannot exceed 500 characters."
      },
      revisionEligibilityCriteria: {
        max: "Cannot exceed 500 characters."
      },
      protocolType: {
          required: "Required Field",
        min: "Required Field"
      },
      indType: {
          required: "Required Field",
        min: "Required Field"
      },
      indAgent: {
        max: "Cannot exceed 100 characters.",
          required: "Required Field"
      },
      indHolder: {
        max: "Cannot exceed 100 characters.",
          required: "Required Field"
      },
      indNumber: {
        max: "Cannot exceed 100 characters.",
          required: "Required Field"
      },
      tentativeActivationDate: {
          required: "Required Field",
        min: "Required Field"
      },
      openContact: {
          required: "Required Field",
        min: "Required Field"
      },
      raveContact: {
          required: "Required Field",
        min: "Required Field"
      },
      otherEmailContacts: {
        test: "Each email must be valid and separated by comma."
      }
    },
    raveSetupForm: {
      isRaveProtocol: {
        required: "Required Field"
      },
      raveInstanceUrl: {
        required: "Required Field"
      },
      isUatStudyNameAsProd: {
        required: "Required Field"
      },
      uatRaveStudyName: {
        max: "Cannot exceed 100 characters.",
        required: "Required Field"
      },
      openRaveAlsVersion: {
        required: "Required Field"
      },
      isRaveCtepAersInt: {
        required: "Required Field"
      },
      ctepAersStepNumber: {
        typeError: "Must be a number.",
        min: "Must be at least 1.",
        max: "Cannot exceed 2 digits.",
        required: "Required Field"
      },
      isTsdvSiteAuditing: {
        required: "Required Field"
      },
      isDqpSupported: {
        required: "Required Field"
      },
      isStudyRaveCalendaring: {
        required: "Required Field"
      },
      isSdpSupported: {
        required: "Required Field"
      },
      cmEffectiveDt: {
        required: "Required Field"
      },
      cmStepNumber: {
        typeError: "Must be a number.",
        min: "Must be at least 1.",
        max: "Cannot exceed 2 digits.",
        required: "Required Field"
      },
      cmPatientSelectionMethod: {
        required: "Required Field"
      },
      patientNumberSchema: {
        typeError: "Must be a number.",
        max: "Cannot exceed 4 digits.",
        required: () => "Required Field"
      },
      isMedidataPatientCloudEpro: {
        required: "Required Field"
      },
      isRequiredTriadTesting: {
        required: "Required Field"
      }
    },
    openSetupForm: {
      ancillaryStudy: {
        groupProtocolNumber: {
          max: "Cannot exceed 100 characters.",
         required: "Required Field"
        },
        stepNumber: {
          typeError: "Must be a number.",
          min: "Step number must be at least 1.",
          max: "Cannot exceed 2 digits.",
         required: "Required Field"
        },
        mandatory: {
         required: "Required Field"
        }
      },
      irocDetails: {
        type: {
          max: "Cannot exceed 100 characters.",
         required: "Required Field"
        },
        requirement: {
         required: "Required Field"
        }
      },
      step: {
        required: {
         required: "Required Field"
        },
        publicFormIds: {
          max: "Cannot exceed 100 characters.",
          required: "Required Field"
        },
        crfVersionNum: {
          max: "Cannot exceed 100 characters.",
          required: "Required Field"
        },
        stepNumber: {
          max: "Cannot exceed 2 characters.",
          required: "Required Field"
        },
        stepDescription: {
          max: "Cannot exceed 100 characters.",
          required: "Required Field"
        },
        boolSelect: {
           required: "Required Field"
        }
      },
      isStandardRandoNodeUrl: {
          required: "Required Field"
      },
      randoNodeUrl: {
        max: "Cannot exceed 200 characters.",
          required: "Required Field",
        url: "Enter a valid URL."
      },
      isEmbeddedAncillaryStudy: {
          required: "Required Field"
      },
      isSlotReservationProtocol: {
          required: "Required Field"
      },
      slotReservationSteps: {
          required: "Required Field",
        test: "Each step must be max 2 digits, separated by comma."
      },
      isIrocCredentialingInfoOpen: {
          required: "Required Field"
      },
      requestedTestSites: {
        max: "Cannot exceed 100 characters.",
          required: "Required Field"
      },
      additionalSetupNotes: {
        max: "Cannot exceed 1000 characters."
      }
    },
    pendingSignOffForm: {
      groupAdmin: {
        releaseDateTime: "Required Field",
        groupAdminSignOff: "Required Field",
        groupAdminComments: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      },
      openAdmin: {
        openAdminSignOff: "Required Field",
        openAdminComments: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      },
      irocAdmin: {
        irocAdminSignOff: "Required Field",
        irocAdminComments: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      }
    },
    groupTestingForm: {
      comments: {
        max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`,
          required: "Required Field"
      }
    },
    openRaveSubmitForm: {
      comments: {
        max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      },
      duplicateCtsuUser: "The same CTSU User is selected for Primary and Secondary OPEN Admin. Please select two different users.",
      primaryComments: {
        max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      },
      secondaryComments: {
        max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      }
    },
    ctsuTestingForm: {
      comments: {
        max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`,
          required: "Required Field"
      }
    },
    completedForm: {
      comments: {
        max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
      }
    }
  },
  regulatory: {
    regulatorySetupForm: {
      protocolVersionDt: {
        required: "Required Field"
      },
      protocolVersionNumber: {
        max: "Cannot exceed 100 characters.",
        required: "Required Field"
      },
      levelIrbReviewRequired: {
        required: "Required Field"
      },
      ohrpCategory: {
        required: "Required Field"
      },
      whenExpeditedReviewAllowed: {
        required: "Required Field"
      },
      releaseDt: {
        required: "Required Field"
      },
      ctsuCollectionRequired: {
        boolean: "Select yes or no.",
        required: "Required Field"
      },
      requiredByDt: {
        required: "Required Field"
      },
      anticipatedPrimaryCompletionDt: {
        required: "Required Field"
      },
      isCollectIrbApprovalPostClosure: {
        boolean: "Select yes or no.",
        required: "Required Field"
      },
      countries: {
        required: "Required Field",
        min: "Required Field"
      },
      isCtaRequired: {
        boolean: "Select yes or no."
      },
      ctaRequiredInfo: {
        max: "Cannot exceed 100 characters.",
          required: "Required Field"
      },
      participationChangeDesc: {
        max: "Cannot exceed 500 characters."
      },
      indAgent: {
        max: "Cannot exceed 100 characters.",
          required: "Required Field"
      },
      indHolder: {
          required: "Required Field"
      },
      indNumber: {
        max: "Cannot exceed 100 characters.",
          required: "Required Field"
      },
      psrDetails: {
          required: "Required Field",
        min: "Required Field"
      },
      attachPSR: {
        test: "Only .doc, .pdf, .msg, .xlsx files are allowed."
      }
    },
    infoForm: {
      requestType: {
        required: "Required Field"
      },
      primaryContact: {
        min: "Required Field"
      }
    },
    psrForm: {
      psrName: {
        required: "Required Field",
        max: "Cannot exceed 100 characters."
      },
      psrVersionDt: {
        required: "Required Field"
      },
      psrType: {
        required: "Required Field"
      },
      psrRequiredByDt: {
        required: "Required Field",
        future: "Date must be today or in the future."
      },
      isEvaluationByGroup: {
        required: "Required Field"
      },
      sourceSubmittedBy: {
        required: "Required Field"
      },
      ctsuProcessingInstructions: {
        max: "Cannot exceed 4000 characters.",
        required: "Required Field"
      },
      psrComments: {
        max: "Cannot exceed 500 characters."
      },
      psrContactName: {
        max: "Cannot exceed 100 characters.",
          required: "Required Field"
      },
      psrContactPhoneNum: {
        max: "Cannot exceed 100 characters.",
          required: "Required Field"
      },
      psrContactEmailAddress: {
        max: "Cannot exceed 100 characters.",
          required: "Required Field"
      },
      psrDistributionList: {
        max: "Cannot exceed 500 characters."
      }
    },
    signOff: {
        acknowledgmentRequired: "Required Field",
      submitForm: {
        groupAdminComments: {
          max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
        }
      },
      inProgress: {
        regComments: {
          max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`,
            required: "Required Field"
        },
        isRegAckCompletedYn: {
            required: "Required Field"
        }
      },
      additional: {
        irocAdminComments: {
          max: `Cannot exceed ${COMMENTS_MAX_LENGTH} characters.`
        }
      }
    }
  }
};

export const titleAndAria = {
  statusRetired: 'Retired',
  statusCanceled: 'Canceled',
  statusCompleted: 'Completed',
  updateNotAllowed: (title, status) => `Update to ${title} is not allowed when request status is ${status}.`,
  expandCollapse: (isOpen, title) => `${isOpen ? "Collapse" : "Expand"} ${title} Section`,
  cannotExpandCollapse: (title, status) => `${title} section cannot be expanded or collapsed. \nEditing is not allowed when request status is ${status}.`,
  viewPsr: "View PSR Requirement",
  editPsr: "Edit PSR Requirement",
  removePsr: "Remove PSR from request",
  makesChangesExistingPsr: "Makes change(s) to an existing PSR",
  addPsr: "Add PSR",
  newPsr: "New PSR",
  addNewProtocolSetupRequest: "Add new protocol setup request",
  openSupplementalChecklist: "Open the Supplemental Checklist in a new tab",
  viewRequest: (requestNumber) => `View Request ${requestNumber}`,
  clearFilters: "clear filters",
  viewRequestHistory: (requestNumber) => `View Request ${requestNumber} history`,
  submitRequest: "Submit Request",
  closeNotification: "Close notification",
  clearChanges: "Clear changes",
  noChangesToClear: "No changes to clear",
  save: "Save",
  saveRequest: "Save request",
  addStudy: "Add Study",
  deleteRow: "Delete this row",
  deleteRowConfirmation: "Are you sure you want to delete this row?",
  addType: "Add Type",
  addStep: "Add Step",
  sendReminder: "Send Reminder",
  saveLabel: (saveLabel, formTitle) => `${saveLabel} ${formTitle || ''}`,
  undoTitle: (title) => `Undo ${title}`,
  viewLatestPart: (formName, requestNumber) => `View latest ${formName} part for Request ${requestNumber} in new tab`,
  attachmentRedactionWarning: (label) => `${label} (attachments should be redacted, remove PII)`,
  delete: "Delete",
  addNewRow: "Add New Row"
};

export default errorMessages;
